/**
 * Armadillo Adamantium Next — Supply Chain Integrity Monitor
 *
 * Detects supply chain attacks by monitoring:
 * - Subresource Integrity (SRI) hash changes for CDN scripts
 * - DOM integrity violations (injected scripts, modified elements)
 * - Unexpected network requests to unknown domains
 * - Script injection via browser extensions or XSS
 *
 * Inspired by the xz-utils backdoor (CVE-2024-3094) pattern:
 * an attacker compromised a dependency to inject a backdoor.
 */

import { sha256 } from 'js-sha256';
import { supabase } from '@/integrations/supabase/client';

// ============================================
// TYPES
// ============================================

export interface IntegrityCheck {
  resource_url: string;
  resource_type: 'script' | 'style' | 'api_endpoint' | 'cdn_resource';
  expected_hash: string;
  actual_hash: string;
  status: 'verified' | 'tampered' | 'changed' | 'missing' | 'unknown';
  timestamp: number;
}

export interface DOMIntegrityViolation {
  type: 'injected_script' | 'modified_element' | 'unexpected_iframe' | 'inline_script';
  element: string;
  details: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

// ============================================
// SUBRESOURCE INTEGRITY MONITOR
// ============================================

class SupplyChainMonitor {
  private static instance: SupplyChainMonitor;
  private knownScripts: Map<string, string> = new Map(); // URL → expected hash
  private knownDomains: Set<string> = new Set();
  private isMonitoring = false;
  private violations: DOMIntegrityViolation[] = [];
  private mutationObserver: MutationObserver | null = null;
  private checkInterval: ReturnType<typeof setInterval> | null = null;

  static getInstance(): SupplyChainMonitor {
    if (!SupplyChainMonitor.instance) {
      SupplyChainMonitor.instance = new SupplyChainMonitor();
    }
    return SupplyChainMonitor.instance;
  }

  /**
   * Start monitoring supply chain integrity
   */
  start() {
    if (this.isMonitoring) return;

    // Snapshot current state
    this.snapshotCurrentResources();

    // Start MutationObserver for DOM integrity
    this.startDOMMonitoring();

    // Periodic integrity checks
    this.checkInterval = setInterval(() => this.runIntegrityCheck(), 60000);

    this.isMonitoring = true;

  }

  /**
   * Stop monitoring
   */
  stop() {
    if (!this.isMonitoring) return;

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    this.isMonitoring = false;
  }

  /**
   * Snapshot all currently loaded scripts and styles
   */
  private snapshotCurrentResources() {
    // Snapshot script tags
    const scripts = document.querySelectorAll('script[src]');
    scripts.forEach((script) => {
      const src = (script as HTMLScriptElement).src;
      if (src) {
        const url = new URL(src, window.location.origin);
        this.knownScripts.set(url.href, ''); // Hash will be computed on first check
        this.knownDomains.add(url.hostname);
      }
    });

    // Add known CDN domains
    const trustedDomains = [
      'cdn.jsdelivr.net',
      'unpkg.com',
      'fonts.googleapis.com',
      'fonts.gstatic.com',
      'orprutybfffdjcihfrki.supabase.co', // Supabase
      'api.ipify.org',
    ];
    trustedDomains.forEach((d) => this.knownDomains.add(d));
  }

  /**
   * Start DOM mutation monitoring
   */
  private startDOMMonitoring() {
    this.mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          for (const node of mutation.addedNodes) {
            if (node instanceof HTMLScriptElement) {
              this.handleNewScript(node);
            } else if (node instanceof HTMLIFrameElement) {
              this.handleNewIframe(node);
            } else if (node instanceof HTMLElement) {
              // React commits add thousands of leaf nodes (spans, icons, text
              // wrappers). Inline scripts can only be injected inside
              // containers, so skip the subtree scan for leaves — this keeps
              // the observer cheap on low-end devices during heavy re-renders.
              if (node.firstElementChild === null) continue;
              const inlineScripts = node.querySelectorAll('script:not([src])');
              if (inlineScripts.length > 0) {
                this.addViolation({
                  type: 'inline_script',
                  element: node.tagName,
                  details: `Inline script injected into ${node.tagName}`,
                  severity: 'high',
                });
              }
            }
          }
        }

        // Detect attribute modifications on script tags
        if (
          mutation.type === 'attributes' &&
          mutation.target instanceof HTMLScriptElement
        ) {
          this.addViolation({
            type: 'modified_element',
            element: 'script',
            details: `Script tag attributes modified: ${mutation.attributeName}`,
            severity: 'critical',
          });
        }
      }
    });

    this.mutationObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'integrity', 'nonce'],
    });
  }

  /**
   * Handle a new script being added to the DOM
   */
  private handleNewScript(script: HTMLScriptElement) {
    const src = script.src;

    if (src) {
      // External script
      try {
        const url = new URL(src, window.location.origin);

        // Check if domain is known
        if (!this.knownDomains.has(url.hostname)) {
          this.addViolation({
            type: 'injected_script',
            element: 'script',
            details: `Script loaded from unknown domain: ${url.hostname} (${src})`,
            severity: 'high',
          });
        }

        // Check for integrity attribute
        if (!script.integrity) {
          this.addViolation({
            type: 'injected_script',
            element: 'script',
            details: `Script loaded without SRI: ${src}`,
            severity: 'medium',
          });
        }
      } catch {
        // Invalid URL
      }
    } else if (script.textContent) {
      // Inline script
      const hash = sha256(script.textContent);
      this.addViolation({
        type: 'inline_script',
        element: 'script',
        details: `Inline script injected (hash: ${hash.substring(0, 16)}...)`,
        severity: 'high',
      });
    }
  }

  /**
   * Handle a new iframe being added
   */
  private handleNewIframe(iframe: HTMLIFrameElement) {
    const src = iframe.src;
    if (src) {
      try {
        const url = new URL(src, window.location.origin);
        if (!this.knownDomains.has(url.hostname)) {
          this.addViolation({
            type: 'unexpected_iframe',
            element: 'iframe',
            details: `Unexpected iframe to: ${url.hostname}`,
            severity: 'medium',
          });
        }
      } catch {
        // Invalid URL
      }
    }
  }

  /**
   * Add a DOM integrity violation
   */
  private addViolation(violation: DOMIntegrityViolation) {
    this.violations.push(violation);
    if (this.violations.length > 100) this.violations.shift();



    // Log to server
    this.logViolationToServer(violation);
  }

  /**
   * Run periodic integrity check on loaded resources
   */
  private async runIntegrityCheck() {
    const scripts = document.querySelectorAll('script[src]');
    const currentUrls = new Set<string>();

    scripts.forEach((script) => {
      const src = (script as HTMLScriptElement).src;
      if (src) currentUrls.add(src);
    });

    // Check for scripts that disappeared (possible tampering)
    for (const [url] of this.knownScripts) {
      if (!currentUrls.has(url)) {
        this.addViolation({
          type: 'modified_element',
          element: 'script',
          details: `Known script removed from DOM: ${url}`,
          severity: 'medium',
        });
      }
    }
  }

  /**
   * Log violation to server
   */
  private async logViolationToServer(violation: DOMIntegrityViolation) {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      await supabase.from('security_events').insert({
        user_id: user?.id || null,
        event_type: `supply_chain_${violation.type}`,
        severity: violation.severity,
        message: violation.details,
        metadata: { element: violation.element, type: violation.type },
        ip_address: null,
        user_agent: navigator.userAgent,
      });
    } catch {
      // Best effort
    }
  }

  /**
   * Get all recent violations
   */
  getViolations(): DOMIntegrityViolation[] {
    return [...this.violations];
  }

  /**
   * Get integrity report
   */
  getReport() {
    return {
      isMonitoring: this.isMonitoring,
      knownScripts: this.knownScripts.size,
      knownDomains: this.knownDomains.size,
      violations: this.violations.length,
      recentViolations: this.violations.slice(-5),
    };
  }
}

export const supplyChainMonitor = SupplyChainMonitor.getInstance();

// ============================================
// POLISH: BUILD-TIME SCANNING, SBOM, LOCKFILE DIFF
// ============================================

import { SBOMEntry, LockfileDiff } from './types';

const sbomEntries = new Map<string, SBOMEntry>();
const lockfileDiffs: LockfileDiff[] = [];

/**
 * Register an SBOM entry from build-time dependency scan
 */
export function registerSBOMEntry(entry: SBOMEntry): void {
  sbomEntries.set(`${entry.packageName}@${entry.version}`, entry);
}

/**
 * Generate full SBOM (Software Bill of Materials)
 */
export function generateSBOM(): SBOMEntry[] {
  return Array.from(sbomEntries.values());
}

/**
 * Check for known vulnerabilities in SBOM entries
 */
export function checkSBOMVulnerabilities(): Array<{ pkg: string; vulnerability: string }> {
  const issues: Array<{ pkg: string; vulnerability: string }> = [];
  for (const [key, entry] of sbomEntries) {
    if (entry.vulnerabilities.length > 0) {
      for (const vuln of entry.vulnerabilities) {
        issues.push({ pkg: key, vulnerability: vuln });
      }
    }
  }
  return issues;
}

/**
 * Compute lockfile diff between two versions
 */
export function computeLockfileDiff(
  oldDeps: Record<string, string>,
  newDeps: Record<string, string>
): LockfileDiff {
  const added: string[] = [];
  const removed: string[] = [];
  const updated: string[] = [];
  const integrityChanged: string[] = [];
  
  for (const [pkg, version] of Object.entries(newDeps)) {
    if (!oldDeps[pkg]) added.push(pkg);
    else if (oldDeps[pkg] !== version) updated.push(pkg);
  }
  for (const pkg of Object.keys(oldDeps)) {
    if (!newDeps[pkg]) removed.push(pkg);
  }
  
  const diff: LockfileDiff = { added, removed, updated, integrityChanged, timestamp: Date.now() };
  lockfileDiffs.push(diff);
  if (lockfileDiffs.length > 50) lockfileDiffs.shift();
  return diff;
}

/**
 * Get lockfile diff history
 */
export function getLockfileDiffHistory(): LockfileDiff[] {
  return [...lockfileDiffs];
}
